import React, { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from "../UI";
import ConfigureExperience from '../Experience/ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import tickImg from '../../assets/tick-step.svg';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { createExperience, createTenantActivityOffers, getAEMContentFragments, getAEMFragmentJsonPath, updateAEMContentFragments } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { compareJson, getNestedData, isValidJsonString } from '../../utils/helper';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import Input from '../UI/Input';
import { permission } from '../../config/permission';
import OfferCode from '../Offers/offerCode';
import DropdownButton from './DropdownButton';
import CreateOfferAndExperienceModal from './CreateOfferAndExperienceModal';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import EditJsonPathModal from './EditJsonPathModal';
import Activation from '../ExperienceCode/Activation';
import { usePreviewMode } from '../../contexts/PreviewModeContext';

const AEMContentDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enums, content: contentfulData } = useContentfulContext();
    const { enablePreviewMode, disablePreviewMode } = usePreviewMode();

    const [currentStep, setCurrentStep] = useState('choose-template');
    const [isStepperHidden, setIsStepperHidden] = useState(false);
    const [aemContentName, setAemContentName] = useState('');
    const [aemContentTitle, setAemContentTitle] = useState("");
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { userData, checkPermission } = useUserContext();
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [fieldValues, setFieldValues] = useState();
    const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
    const [aemContent, setAemContent] = useState();
    const [aemContentAllData, setAemContentAllData] = useState()
    const [originalAemContent, setOriginalAemContent] = useState();
    const [validationError, setValidationError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [showCreateOfferModal, setShowCreateOfferModal] = useState();
    const [type, setType] = useState('');
    const [jsonPathModal, setJsonPathModal] = useState({isOpen:false, type : null,  aemContent:null });
    const [experience, setExperience] = useState(null);

    useEffect(() => {
      getAEMContents()
    }, [])

    useEffect(() => {
      const timer = setTimeout(() => {
        setError('');
        setSuccessMessage('');
      }, 4000);
  
      return () => clearTimeout(timer);
    }, [error, successMessage]);

    const jsonPathModalClose = () => {
      // If the state hasn't been updated or user hasn't made changes
      if (!aemContent || !originalAemContent) {
        navigate("/experience/aem-content-fragments");
      } else {
        setJsonPathModal({
          isOpen: false,
          type: "",
          aemContent: null
        });
      }
    };
    

    const updateAEMContentFromPath = (jsonPath, fullData) => {
      const pathData = getNestedData(fullData, jsonPath);
    
      if (pathData && pathData.value && pathData[":type"]?.toLowerCase() ==  "json") {
        const temp = pathData.value;
        const type = pathData[":type"] || "";
        const name = pathData?.title || "";
        setAemContent(temp);
        setOriginalAemContent(temp);
        setType(type);
        setAemContentName(name);
        return true;
      }else{
        if(pathData && pathData[":type"]?.toLowerCase() !==  "json"){
          toast.error("Selected fragment is not of type JSON");
          navigate("experience/aem-content-fragments");
          return;
        }else{
          return null;
        }
      }
      return pathData;
    };    

    const getAEMContents = async () => {
      try {
        if (!userData?.defaultAEMAdobeIO) {
          toast.error(contentfulData.AEM_CONTENT_DETAILS.errors.no_aem_found);
          navigate("experience/aem-content-fragments");
          return;
        }
        setLoading(true);
        let payload = {
          token: userData.token,
          organizationId: userData.selectedOrganization.org_token,
          aem_adobe_io_id: userData.defaultAEMAdobeIO._id,
          name: id
        };

        if (userData.defaultAEMFolder?.href) {
          payload.aem_folder_url = userData.defaultAEMFolder.href;
        }
        // Get AEM fragments from API
        const result = await getAEMContentFragments(payload);

        setAemContentAllData(result.data);
        const elements = result.data?.entities?.properties?.elements || {};
        const elementKeys = Object.keys(elements);
   

        let resultFragments;
        // step 1: check with default path
        if(elementKeys && elementKeys.length){
          setAemContentTitle(elementKeys[0])
          resultFragments = updateAEMContentFromPath(`entities.properties.elements.${elementKeys[0]}`, result.data)
        }else {
          resultFragments = null;
        }
          
        if(!resultFragments) {
            //step 2: check path from DB
            const aemJsonPath = await getAEMFragmentJsonPath(
              userData.token,
              userData.selectedOrganization.org_token,
              userData.defaultAEMAdobeIO._id
            );

            if (aemJsonPath.status && aemJsonPath?.data) {

              // scenario: when path mapping exist for give content fragment
              const jsonPath = aemJsonPath.data?.json_path;
  
              const response = updateAEMContentFromPath(jsonPath, result.data)
              if(!response) {
                // if json is not present on the available  mapping path
                setError(contentfulData.AEM_CONTENT_DETAILS.errors.get_saved_path);
                setJsonPathModal({
                  isOpen: true,
                  type: "edit",
                  aemContent: result.data
                });
              }
            }else {
              // when the path does not exist in the db
              setJsonPathModal({
                isOpen: true,
                type: "add",
                aemContent: result.data
              });
            }
          }
      } catch (error) {
        toast.error(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg);
        navigate("experience/aem-content-fragments");
      } finally {
        setLoading(false);
      }
    }; 

    function getFlattenExperienceJSON(ob) {
      let result = {};
      function recurse(cur, prop) {
        if (Object(cur) !== cur) {
          result[prop] = cur;
        } else if (Array.isArray(cur)) {
          for (let i = 0; i < cur.length; i++) {
            recurse(cur[i], prop ? prop + "[" + i + "]" : i);
          }
          if (cur.length === 0) result[prop] = [];
        } else {
          let isEmpty = true;
          for (let p in cur) {
            isEmpty = false;
            recurse(cur[p], prop ? prop + "." + p : p);
          }
          if (isEmpty && prop) result[prop] = {};
        }
      }
      recurse(ob, "");
      return result;
    }

    const createOffer = async (values) => {
      try {
        if(!aemContentName) {
          setError(contentfulData.AEM_CONTENT_DETAILS.errors.name_required)
          return;
        }
        if(validationError) return;

        if(showPreview) {
          const isValidate = handleValidateJson();
          if (isValidate.length > 0) return;
        }
        const content = JSON.parse(aemContent)

        const payload = { 
          tenant: userData.defaultAdobeIO?.tenant_name,
          data: {
            name: values.offer_name,
            workspace: userData.defaultWorkspace?.workspace_id,
            content: showPreview ? transformOfferJSON() : content,
            type: selectedTemplate.type === enums.templateTypes.json_a_to_html.id ? 'content' : 'json',
          }
        }
        const result = await createTenantActivityOffers(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          payload
        )
        return result;
      } catch (error) {
        throw error;
      }
    };

  const createExperienceHandler = async (values, offerId) => {
    try {
      if (!selectedTemplate) {
        setError(contentfulData.AEM_CONTENT_DETAILS.errors.template_required);
        return;
      }

      if (showPreview) {
        const isValidate = handleValidateJson();
        if (isValidate.length > 0) {
          setError(isValidate.join(", \n"))
          return
        }
      }

      if (validationError) {
        setError(validationError);
        return
      }

      const content = JSON.parse(aemContent)

      const payload = {
        name: values.experience_name,
        templates: [selectedTemplate._id],
        html: '',
        css: '',
        config_json: '',
        experience_json: showPreview ? JSON.stringify(transformOfferJSON()) : JSON.stringify(content),
        source: enums.experience_sources.offer_page.id,
      }
      const result = await createExperience(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload
      );
      setExperience(result?.data)
      return result;
    } catch (error) {
      throw error;
    }
  }

  const handleCheckValidation = () => {
    const isValidJson = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? true : isValidJsonString(aemContent)
    if (!isValidJson) {
        setValidationError(contentfulData.EDIT_EXPERIENCE.invalid_json);
        return;
    }
    const isValidate = handleValidateJson();
    if (isValidate.length > 0) {
        setError(isValidate.join(", \n"))
        return;
    }
    setError("");
    return true;
}

  const handleUpdateAEM = async () => {
    try {
      // if (selectedTemplate.type != enums?.templateTypes.no_preview.id) return;
      if (!handleCheckValidation()) return;
      setLoading(true)
      const putURL = aemContentAllData?.entities?.links?.find((x) => x?.rel?.includes("self"))?.href;
      const payload = {
        aem_adobe_io_id: userData?.defaultAEMAdobeIO?._id,
        template: selectedTemplate?._id,
        scope: selectedTemplate?.scope,
        value: {
          properties: {
            elements: {
              [aemContentTitle]: {
                value: showPreview ? JSON.stringify(transformOfferJSON()) : aemContent
              }
            }
          }
        }
      };
      const result = await updateAEMContentFragments(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        payload,
        putURL
      )
      if(result?.status){
        toast.success(result?.message)
      }
      return result
    } catch (error) {
      throw error;
    } finally{
      setLoading(false)
    }
  }

  useEffect(()=>{
    if(currentStep == 'choose-template' && selectedTemplate._id) {
      if(!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(contentfulData.AEM_CONTENT_DETAILS.errors.valid_json)
        return;
      }
      const AEMJson = JSON.parse(aemContent)
      const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), AEMJson);
      if ((compare.length == 0) || (compare.length == 1 && compare.includes("experience_id"))) {
          setFieldValues(getFlattenExperienceJSON(AEMJson));
      } else {
          setError(`${compare.join(", ")}${contentfulData.AEM_CONTENT_DETAILS.errors.templates_not_same}`)
          return;
      }
    }
    if(selectedTemplate?._id && currentStep == 'choose-template') {
      setCurrentStep('configure-offer');
  }
  },[selectedTemplate])

    const handleNext = () => {
      if(currentStep == 'choose-template' && !selectedTemplate._id) {
          setError(contentfulData.AEM_CONTENT_DETAILS.errors.template_required);
          return;
      }
      if(currentStep == 'choose-template' && selectedTemplate._id) {
        if(!isValidJsonString(selectedTemplate?.json_schema)) {
          setError(contentfulData.AEM_CONTENT_DETAILS.errors.valid_json)
          return;
        }
        const AEMJson = JSON.parse(aemContent)
        const compare = compareJson(JSON.parse(selectedTemplate?.json_schema), AEMJson);
        if(compare.length == 0) {
            setFieldValues(getFlattenExperienceJSON(AEMJson));
        } else {
            setError(`${compare.join(", ")}${contentfulData.AEM_CONTENT_DETAILS.errors.templates_not_same}`)
            return;
        }
      }
      if(currentStep == 'choose-template') {
          setCurrentStep('configure-offer');
      } else if(currentStep == 'configure-offer') {
          setCurrentStep('update-offer');
      }
    }

    function transformOfferJSON() {
      const transformedJSON = {};
      for (const key in fieldValues) {
        const keys = key.split('.');
        keys.reduce((acc, part, index) => {
          if (part.includes('[')) {
            const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
            if (!acc[arrayKey]) {
              acc[arrayKey] = [];
            }
            if (!acc[arrayKey][arrayIndex]) {
              acc[arrayKey][arrayIndex] = {};
            }
            if (index === keys.length - 1) {
              acc[arrayKey][arrayIndex] = fieldValues[key];
            }
            return acc[arrayKey][arrayIndex];
          } else {
            if (index === keys.length - 1) {
              acc[part] = fieldValues[key];
            } else {
              if (!acc[part]) {
                acc[part] = {};
              }
              return acc[part];
            }
          }
        }, transformedJSON);
      }
      return transformedJSON;
    }

    const handleValidateJson = (obj) => {
      setValidationError("");
      // const json = obj || selectedTemplate.config_json;

      const config_elems = [];
      JSON.parse(selectedTemplate.config_json).forEach((el) => {
          el.error = "";
          config_elems.push({ ...el, id: el.id.replace("[", ".").replace("]", "") });
      });
      const newConfigElems = [...config_elems];
      const errs = [];
  
      for (let item of newConfigElems) {
          const val = fieldValues[item.id];
          const rules = item.rules;
          if (rules.is_mandatory) {
              if (val?.length == 0) {
                  const message = "Field is required";
                  item.error = message;
                  errs.push(`${item.id}: ${message}`)
              } else {
                  item.error = "";
              }
          }
          if (rules.min_length && rules.max_length && item.error.length == 0) {
              if (val.length > rules.max_length || val.length < rules.min_length) {
                  const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
                  item.error = message;
                  errs.push(`${item.id}: ${message}`);
              } else {
                  item.error = "";
              }
          }
      }
  
      if(errs.length > 0) {
          setValidationError(errs.join(", \n"));
      }
      return errs;
    }

    const handleSubmit = async (action, values) => {
      try {
        setError('');
        setLoading(true);
        let successMsg, result;
        switch (action) {
          case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.id:
            if(!values?.offer_name) {
              setError("Offer name is required.")
              return;
            }
            result = await createOffer(values);
            successMsg = `${contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.success}. Offer Id - ${result.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
            break;

          case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.save_experience.id:
            if(!values?.experience_name) {
              setError("Experience name is required.")
              return;
            }
            result = await createExperienceHandler(values);
            successMsg = contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.save_experience.success
            break;
          
          case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.both.id:
            if(!values?.experience_name || !values?.offer_name) {
              setError("Fields are required.")
              return;
            }
            const offerResult = await createOffer(values);
            result = await createExperienceHandler(values, offerResult?.data?.id);
            successMsg = `${contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.both.success}. Offer Id - ${offerResult.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
            break;
        
          default:
            break;
        }

        if(result) {
          setSuccessMessage(successMsg);
          setTimeout(() => {
            navigate("/experience/aem-content-fragments")
          }, 1500);
        }
      } catch (error) {
        console.log('error', error)
        setError(error?.message || contentfulData?.COMMON_MESSAGE.something_wrong_msg);
      } finally {
        setLoading(false);
      }
    };

    const handleAction = (action) => {
      switch (action) {
        case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.update_aem.id:
          handleUpdateAEM()
          break;
        case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.id:
          setCurrentStep("activate")
          break;
        case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.save_experience.id:
          setShowCreateOfferModal({
            action,
            fields: [contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.save_experience.id]
          })
          break;
        case contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.both.id:
          setShowCreateOfferModal({
            action,
            fields: [contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.id, contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.save_experience.id]
          })
          break;
        default:
          console.log(`Unknown action: ${action}`);
      }
    }

    const handleCloseModal = () => {
      setShowCreateOfferModal(null)
    }

    useEffect(() => {
      if(currentStep == "configure-offer") {
        enablePreviewMode();
      } else {
        disablePreviewMode();
      }
    }, [currentStep])

    return (
        <div className='w-full mx-auto overflow-auto sticky z-[15]'>
            { loading && <Loader /> }
            { error && <ErrorAlert timeout="3000">{error}</ErrorAlert> }
            { successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert> }
            <div className='py-2 px-4 md:px-8 bg-gray-1 mb-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
                <div className='w-full md:max-w-[260px]'>
                    <Input
                        size="sm"
                        disabled
                        id="offer_name"
                        name="offer_name"
                        value={aemContentName}
                        placeholder={contentfulData.AEM_CONTENT_DETAILS.aem_content_name}
                    />
                </div>
                {!showPreview && !isStepperHidden && <div className='flex justify-end gap-3'>
                  {checkPermission(permission.AEM_CONTENT_FRAGMENT_OP_CREATE_PREVIEW) && (
                    <SecondaryButton size={'base'} handleClick={() => setShowPreview(true)}>
                      {contentfulData.AEM_CONTENT_DETAILS.show_preview_cta}
                    </SecondaryButton>
                  )}
                  {checkPermission(permission.AEM_CONTENT_FRAGMENT_OP_OFFER) && (
                    <PrimaryButton
                      size={'base'}
                      handleClick={() =>{
                        setCurrentStep("activate")
                        setIsStepperHidden(true)
                      }}
                    >
                      {contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.name}
                    </PrimaryButton>
                  )}
                </div>}
                {showPreview && <ol className="flex items-center justify-center flex-1 text-xs font-medium text-center text-gray-500 gap-2 md:gap-0">
                    <li className="text-xs flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10">
                        <button className="flex items-center flex-col after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200" onClick={() => {
                            setAemContent(originalAemContent)
                            setCurrentStep("choose-template");
                            setSelectedTemplate({})
                            setFieldValues()
                          }}
                        >
                            <img alt='' src={tickImg} className="w-4 h-4" />
                            <span className="hidden lg:block">{contentfulData.AEM_CONTENT_DETAILS.choose_template}</span>
                        </button>
                    </li>
                    <li className="text-xs flex items-center">
                        <button className="flex items-center flex-col" onClick={() => {
                          if(currentStep == "update-offer") {
                            setCurrentStep("configure-offer")
                          }
                        }}>
                            { (currentStep == 'configure-offer' || currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">2</span> }
                            <span className="hidden lg:block">{contentfulData.AEM_CONTENT_DETAILS.configure_aem_content}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            { (currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">3</span> }
                            <span className="hidden lg:block">{contentfulData.AEM_CONTENT_DETAILS.update_aem_content}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            { (currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">4</span> }
                            <span className="hidden lg:block">{contentfulData.AEM_CONTENT_DETAILS.activate}</span>
                        </button>
                    </li>
                </ol>}
                {showPreview && <div className='flex justify-end gap-2 md:gap-3 w-full md:w-auto'>
                  {currentStep !== 'activate' && <SecondaryButton size={'base'} handleClick={() => navigate("/experience/aem-content-fragments")}>{contentfulData.AEM_CONTENT_DETAILS.cancel_btn}</SecondaryButton>}
                  {currentStep == "update-offer" ? (
                    checkPermission(permission.EDIT_OFFER) && <DropdownButton handleSubmit={handleAction} />
                  ) : (currentStep !== "choose-template"  && currentStep !== 'activate' )? (
                    <PrimaryButton size={'base'} handleClick={handleNext}>{contentfulData.AEM_CONTENT_DETAILS.next_btn}</PrimaryButton>
                  ) : null}
                </div>}
            </div>
            <div className='px-4 md:px-8'>
                { currentStep == 'choose-template' && aemContent && (
                  <ChooseTemplate
                    title={contentfulData.CHOOSE_TEMPLATE_CONTENT.fragment_content}
                    offerJson={{ content: aemContent ? JSON.parse(aemContent) : "", isCreateOffer: true, offerType: "json" }}
                    selectedTemplate={selectedTemplate}
                    setSelectedTemplate={setSelectedTemplate}
                    hideTemplates={!showPreview}
                    type={type}
                  />
                )}
                { currentStep == 'configure-offer' && (
                  <ConfigureExperience
                    setCurrentStep={setCurrentStep}
                    selectedTemplate={selectedTemplate}
                    setSelectedTemplate={setSelectedTemplate}
                    fieldValues={fieldValues}
                    setFieldValues={setFieldValues}
                    setTransformedExperienceJSON={setTransformedExperienceJSON}
                    actionText={contentfulData.AEM_CONTENT_DETAILS.edit_aem_fragment}
                  />
                )}
                { currentStep == 'update-offer' && 
                  <OfferCode
                    title={contentfulData.CHOOSE_TEMPLATE_CONTENT.fragment_content}
                    experienceJSON={transformedExperienceJSON}
                    selectedTemplate={selectedTemplate}
                    offerJson={aemContent}
                    getFlattenExperienceJSON={getFlattenExperienceJSON}
                    setFieldValues={setFieldValues}
                    validationError={validationError}
                    setValidationError={setValidationError}
                    handleValidateJson={handleValidateJson}
                  />
                }
                {currentStep == 'activate' &&
                <>
                  <h1 className="text-xl px-6 py-4 font-extrabold leading-tight tracking-tight text-gray-900 md:text-2xl">{contentfulData.AEM_CONTENT_DETAILS.dropdown_btn.activate_at.name}</h1>
                  <Activation
                    experience={experience}
                    setExperience={setExperience}
                    validationError={validationError}
                    setValidationError={setValidationError}
                    handleValidateJson={handleValidateJson}
                    offerPermission={{
                      create: permission.CREATE_EXP_OP_CREATE_OFFER
                    }}
                    contentfulPermission={{
                      create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
                    }}
                    selectedTemplate={isStepperHidden ? null :selectedTemplate}
                    code={showPreview ? transformOfferJSON() : JSON.parse(aemContent)}
                    offerActionConfig={{   /* passed config with create offer handler*/
                      handler: createOffer, /* call this handle for create offer if passed*/
                      source: "aem"
                    }}
                  />
                </>
                }

                {showCreateOfferModal && (
                  <CreateOfferAndExperienceModal
                    error={error}
                    title={contentfulData.AEM_CONTENT_DETAILS.title}
                    loading={loading}
                    handleSubmit={handleSubmit}
                    handleCloseModal={handleCloseModal}
                    offerSuccessMessage={successMessage}
                    showCreateOfferModal={showCreateOfferModal}
                  />
                )}

                {
                  jsonPathModal?.isOpen && jsonPathModal?.aemContent && (
                    <EditJsonPathModal
                      onClose={jsonPathModalClose}
                      type={jsonPathModal?.type}
                      data={jsonPathModal?.aemContent}
                      onSuccess={updateAEMContentFromPath}
                      source="aem"
                    />
                  )
                }
            </div>
        </div>
    )
}

export default AEMContentDetails;